/**
 * Superfleet.ai Confidential
 * ----------------------
 * Created by Tigran Yengibaryan on 2/25/2024
 * All Rights Reserved.
 */
import { Box, Tooltip } from '@mui/material';
import { forwardRef } from 'react';
import { t } from 'i18next';
import { style } from './style';
import { SFControlledInputProps } from '../SFTextField';
import { noop } from '../../utils';

export enum ColorLabel {
  PINK = '#E91E63',
  RED = '#F44336',
  DEEP_ORANGE = '#FF5722',
  ORANGE = '#FF9800',
  AMBER = '#FFC107',
  LIGHT_YELLOW = '#FFEB3B',
  PURPLE = '#9C27B0',
  DEEP_PURPLE = '#673AB7',
  INDIGO = '#3F51B5',
  BLUE = '#2196F3',
  LIGHT_BLUE = '#4CA7C8',
  CYAN = '#00BCD4',
  TEAL = '#009688',
  GREEN = '#4CAF50',
  LIGHT_GREEN = '#8BC34A',
  YELLOW = '#FFFF00',
}

export const COLORS = [
  {
    id: 0,
    key: ColorLabel.BLUE,
    label: t(`shared.colorPalette.${ColorLabel.BLUE}`),
  },
  {
    id: 1,
    key: ColorLabel.LIGHT_BLUE,
    label: t(`shared.colorPalette.${ColorLabel.LIGHT_BLUE}`),
  },
  {
    id: 2,
    key: ColorLabel.GREEN,
    label: t(`shared.colorPalette.${ColorLabel.GREEN}`),
  },
  {
    id: 3,
    key: ColorLabel.YELLOW,
    label: t(`shared.colorPalette.${ColorLabel.YELLOW}`),
  },
  {
    id: 4,
    key: ColorLabel.RED,
    label: t(`shared.colorPalette.${ColorLabel.RED}`),
  },
  {
    id: 5,
    key: ColorLabel.PURPLE,
    label: t(`shared.colorPalette.${ColorLabel.PURPLE}`),
  },
  {
    id: 6,
    key: ColorLabel.PINK,
    label: t(`shared.colorPalette.${ColorLabel.PINK}`),
  },
  {
    id: 7,
    key: ColorLabel.DEEP_PURPLE,
    label: t(`shared.colorPalette.${ColorLabel.DEEP_PURPLE}`),
  },
  {
    id: 8,
    key: ColorLabel.DEEP_ORANGE,
    label: t(`shared.colorPalette.${ColorLabel.DEEP_ORANGE}`),
  },
  {
    id: 9,
    key: ColorLabel.ORANGE,
    label: t(`shared.colorPalette.${ColorLabel.ORANGE}`),
  },
  {
    id: 10,
    key: ColorLabel.AMBER,
    label: t(`shared.colorPalette.${ColorLabel.AMBER}`),
  },
  {
    id: 11,
    key: ColorLabel.LIGHT_YELLOW,
    label: t(`shared.colorPalette.${ColorLabel.LIGHT_YELLOW}`),
  },
  {
    id: 12,
    key: ColorLabel.CYAN,
    label: t(`shared.colorPalette.${ColorLabel.CYAN}`),
  },
  {
    id: 13,
    key: ColorLabel.TEAL,
    label: t(`shared.colorPalette.${ColorLabel.TEAL}`),
  },
  {
    id: 14,
    key: ColorLabel.LIGHT_GREEN,
    label: t(`shared.colorPalette.${ColorLabel.LIGHT_GREEN}`),
  },
];

export const SFPaletteColorMapping: Record<ColorLabel, string> = {
  [ColorLabel.PINK]: '#c91e43',
  [ColorLabel.YELLOW]: '#ffc107',
  [ColorLabel.PURPLE]: '#7c0790',
  [ColorLabel.RED]: '#d42316',
  [ColorLabel.DEEP_ORANGE]: '#df3712',
  [ColorLabel.ORANGE]: '#df7800',
  [ColorLabel.AMBER]: '#dfa107',
  [ColorLabel.LIGHT_YELLOW]: '#dfcb1b',
  [ColorLabel.DEEP_PURPLE]: '#471a97',
  [ColorLabel.INDIGO]: '#1f3195',
  [ColorLabel.BLUE]: '#1e88e5',
  [ColorLabel.LIGHT_BLUE]: '#4ca7c8',
  [ColorLabel.CYAN]: '#00bcd4',
  [ColorLabel.TEAL]: '#009688',
  [ColorLabel.GREEN]: '#4caf50',
  [ColorLabel.LIGHT_GREEN]: '#8bc34a',
};

type Props = SFControlledInputProps & {
  onBlur?: () => void;
  onChange?: (value: ColorLabel) => void;
  value?: string | string[];
  size?: number;
};

const TooltipPopperProps = {
  modifiers: [
    {
      name: 'offset',
      options: {
        offset: [0, -10],
      },
    },
  ],
};

const SFColorPalette = forwardRef(
  ({ onBlur = noop, onChange = noop, value = '', size = 40 }: Props, ref) => {
    function handleColorChange(label: ColorLabel) {
      onChange(label);
    }

    return (
      <Box sx={style.colors} ref={ref} onBlur={onBlur}>
        {COLORS.map((color) => (
          <Tooltip
            key={color.id}
            title={color.label}
            placement='top'
            PopperProps={TooltipPopperProps}
          >
            <Box
              onClick={() => handleColorChange(color.key)}
              sx={[
                style.coloredCircle(SFPaletteColorMapping[color.key], size),
                typeof value === 'string' &&
                  value === color.key &&
                  style.selectedCircle,
                Array.isArray(value) &&
                  value.includes(color.key) &&
                  style.selectedCircle,
              ]}
            />
          </Tooltip>
        ))}
      </Box>
    );
  },
);

SFColorPalette.displayName = 'SFColorPalette';

export default SFColorPalette;
